import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { ILocationContext, ILocationContextSelection } from '../../../model/location-context';
import { EnvService } from '../configuration/env.service';
import { Pages } from '../pages.config';
import LocationContextActions from './store/location-context.action-types';
import { LocationContextState } from './store/location-context.reducers';
import { centerlineGroupsSelector, centerlinesSelector, terminalsSelector } from './store/location-context.selectors';

@Injectable({
    providedIn: 'root'
})
export class LocationContextService {
    sub: Subscription;

    private readonly locationContextChangesSubject = new Subject<boolean>();
    locationContextChanges$: Observable<boolean> = this.locationContextChangesSubject.asObservable();

    constructor(
        private readonly store: Store<LocationContextState>,
        private readonly env: EnvService,
        private readonly router: Router
    ) {}

    // Selecton config JSON convertion
    getSelectionConfigJson(selection: ILocationContextSelection): string {
        return JSON.stringify(selection);
    }

    getSelectionConfigObject(key: string): ILocationContextSelection {
        const configString = sessionStorage.getItem(key);

        return configString && configString.length > 0 ? JSON.parse(configString) : {};
    }

    // Location Context
    isLocationContextValid(): boolean {
        // Selection
        const selectedTerminal = this.getSelectedTerminal();
        const selectedCenterlineGroup = this.getSelectedCenterlineGroup();
        const selectedCenterline = this.getSelectedCenterline();

        // Validate
        return selectedTerminal !== '' && selectedCenterlineGroup !== '' && selectedCenterline !== '';
    }

    onLocationContextScreen() {
        return this.router.url.startsWith(Pages.locationContext.router);
    }

    // Session
    setSessionItem(key: string, value: string) {
        // Set
        sessionStorage.setItem(key, value);

        // Broadcast
        this.broadcastLocationContextChanges();
    }

    removeSessionItem(key: string) {
        // Remove
        sessionStorage.removeItem(key);

        // Broadcast
        this.broadcastLocationContextChanges();
    }

    // Changes
    broadcastLocationContextChanges() {
        this.locationContextChangesSubject.next(this.isLocationContextValid());
    }

    getLocationContext(): ILocationContext {
        return {
            station: this.env.config.stationId,
            terminal: this.getSelectedTerminal(),
            centerlineGroup: this.getSelectedCenterlineGroup(),
            centerline: this.getSelectedCenterline()
        };
    }

    resetLocationContextData() {
        this.store.dispatch(LocationContextActions.resetLocationContextData());
    }

    resetLocationContextDataAndSelection() {
        // Clear data
        this.resetLocationContextData();

        // Clear selection
        this.clearSelectedTerminal();
        this.clearSelectedCenterlineGroup();
        this.clearSelectedCenterline();

        this.broadcastLocationContextChanges();
    }

    // Terminals
    requestTerminals() {
        this.store.dispatch(LocationContextActions.requestTerminals());
    }

    loadTerminals(terminals: string[]) {
        this.store.dispatch(LocationContextActions.loadTerminals({ terminals: terminals }));
    }

    getTerminals(): Observable<string[]> {
        return this.store.select(terminalsSelector);
    }

    setSelectedTerminal(terminal: string, singleTerminal: boolean) {
        this.setSessionItem(
            'terminalConfig',
            this.getSelectionConfigJson({ selection: terminal, isSingleOption: singleTerminal })
        );
    }

    clearSelectedTerminal() {
        this.removeSessionItem('terminalConfig');
    }

    getSelectedTerminal(): string {
        const config = this.getSelectionConfigObject('terminalConfig');

        return config && config.selection && config.selection.length > 0 ? config.selection : '';
    }

    isTerminalSelected(): boolean {
        return this.getSelectedTerminal() !== '';
    }

    isSingleTerminal(): boolean {
        const config = this.getSelectionConfigObject('terminalConfig');

        return config ? config.isSingleOption : false;
    }

    // Centerline Groups
    requestCenterlineGroups(selectedTerminal: string) {
        this.store.dispatch(LocationContextActions.requestCenterlineGroups({ selectedTerminal }));
    }

    loadCenterlineGroups(centerlineGroups: string[]) {
        this.store.dispatch(LocationContextActions.loadCenterlineGroups({ centerlineGroups }));
    }

    getCenterlineGroups(): Observable<string[]> {
        return this.store.select(centerlineGroupsSelector);
    }

    setSelectedCenterlineGroup(centerlineGroup: string, singleCenterlineGroup: boolean) {
        this.setSessionItem(
            'centerlineGroup',
            this.getSelectionConfigJson({ selection: centerlineGroup, isSingleOption: singleCenterlineGroup })
        );
    }

    clearSelectedCenterlineGroup() {
        this.removeSessionItem('centerlineGroup');
    }

    getSelectedCenterlineGroup(): string {
        const config = this.getSelectionConfigObject('centerlineGroup');

        return config && config.selection && config.selection.length > 0 ? config.selection : '';
    }

    isCenterlineGroupSelected(): boolean {
        return this.getSelectedCenterlineGroup() !== '';
    }

    isSingleCenterlineGroup(): boolean {
        const config = this.getSelectionConfigObject('centerlineGroup');

        return config ? config.isSingleOption : false;
    }

    // Centerlines
    requestCenterlines(selectedCenterlineGroup: string) {
        this.store.dispatch(LocationContextActions.requestCenterlines({ selectedCenterlineGroup }));
    }

    loadCenterlines(centerlines: string[]) {
        this.store.dispatch(LocationContextActions.loadCenterlines({ centerlines: centerlines }));
    }

    getCenterlines(): Observable<string[]> {
        return this.store.select(centerlinesSelector);
    }

    setSelectedCenterline(centerline: string, singleCenterline: boolean) {
        this.setSessionItem(
            'centerline',
            this.getSelectionConfigJson({ selection: centerline, isSingleOption: singleCenterline })
        );
    }

    clearSelectedCenterline() {
        this.removeSessionItem('centerline');
    }

    getSelectedCenterline(): string {
        const config = this.getSelectionConfigObject('centerline');

        return config && config.selection && config.selection.length > 0 ? config.selection : '';
    }

    isCenterlineSelected(): boolean {
        return this.getSelectedCenterline() !== '';
    }

    isSingleCenterline(): boolean {
        const config = this.getSelectionConfigObject('centerline');

        return config ? config.isSingleOption : false;
    }
}
